import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import {
  DataTable,
  type BulkAction,
  type DataTableColumn,
  type PaginationState,
} from '@saas-kit/ui/DataTable';

import type { AdminTenant } from './api';
import { TenantActions } from './TenantActions';
import { TenantStatusBadge } from './TenantStatusBadge';

type ActionKey = 'suspend' | 'reactivate' | 'archive';

interface Props {
  tenants: AdminTenant[];
  pendingTenantId: string | null;
  pendingAction: ActionKey | null;
  pagination: PaginationState;
  isLoading: boolean;
  error: string | null;
  bulkActions: BulkAction<AdminTenant>[];
  onSuspend: (tenant: AdminTenant) => void;
  onReactivate: (tenant: AdminTenant) => void;
  onArchive: (tenant: AdminTenant) => void;
  search: string;
  onSearchChange: (next: string) => void;
  searchPlaceholder: string;
}

export function TenantsTable({
  tenants,
  pendingTenantId,
  pendingAction,
  pagination,
  isLoading,
  error,
  bulkActions,
  onSuspend,
  onReactivate,
  onArchive,
  search,
  onSearchChange,
  searchPlaceholder,
}: Props): JSX.Element {
  const { t, i18n } = useTranslation('adminTenants');

  const dateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }),
    [i18n.language],
  );

  const columns = useMemo<DataTableColumn<AdminTenant>[]>(
    () => [
      {
        id: 'name',
        header: t('table.name'),
        cell: (row) => (
          <Link
            to={`/tenants/${row.id}`}
            className="font-medium text-primary underline-offset-2 hover:underline"
          >
            {row.name}
          </Link>
        ),
      },
      {
        id: 'slug',
        header: t('table.slug'),
        cell: (row) => (
          <span className="font-mono text-xs text-muted-foreground">{row.slug}</span>
        ),
        hideOnMobile: true,
      },
      {
        id: 'status',
        header: t('table.status'),
        cell: (row) => <TenantStatusBadge status={row.status} />,
      },
      {
        id: 'created_at',
        header: t('table.created_at'),
        cell: (row) => (
          <span className="text-muted-foreground">
            {dateFormatter.format(new Date(row.created_at))}
          </span>
        ),
        hideOnMobile: true,
        exportValue: (row) => row.created_at,
      },
      {
        id: 'actions',
        header: t('table.actions'),
        align: 'end',
        exportSkip: true,
        cell: (row) => (
          <TenantActions
            tenant={row}
            pendingAction={pendingTenantId === row.id ? pendingAction : null}
            onSuspend={onSuspend}
            onReactivate={onReactivate}
            onArchive={onArchive}
          />
        ),
      },
    ],
    [
      t,
      dateFormatter,
      pendingTenantId,
      pendingAction,
      onSuspend,
      onReactivate,
      onArchive,
    ],
  );

  return (
    <DataTable<AdminTenant>
      data={tenants}
      columns={columns}
      rowKey={(row) => row.id}
      testId="admin-tenants"
      selectable
      bulkActions={bulkActions}
      pagination={pagination}
      isLoading={isLoading}
      error={error}
      emptyText={t('empty.title')}
      exportable
      exportFilename="admin-tenants"
      search={search}
      onSearchChange={onSearchChange}
      searchPlaceholder={searchPlaceholder}
    />
  );
}
